import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtEUR, fmtDate } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Download, Loader2 } from "lucide-react";

function naarCsv(rijen) {
  const kop = ["Klantcode", "Naam", "Plaats", "Land", "Status", "Webshop", "E-mail", "Telefoon", "Omzet 12m", "Laatste order", "Kenmerken"];
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const body = rijen.map(o => [o.klantcode, o.naam, o.plaats, o.land, o.status, o.webshop_status, o.email, o.telefoon, o.omzet_12m ?? "", o.laatste_order_datum ? o.laatste_order_datum.slice(0, 10) : "", (o.kenmerken || []).join(" | ")].map(esc).join(";"));
  return [kop.map(esc).join(";"), ...body].join("\n");
}

export function SegmentLedenLijst({ organisaties, naam = "segment", maxHoogte = "60vh" }) {
  const [q, setQ] = useState("");
  const rijen = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return organisaties;
    return organisaties.filter(o => [o.naam, o.klantcode, o.plaats, o.land, o.email, ...(o.kenmerken || [])].some(v => (v || "").toString().toLowerCase().includes(t)));
  }, [organisaties, q]);
  const download = () => {
    const blob = new Blob(["\ufeff" + naarCsv(rijen)], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `segment-${naam.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.csv`; a.click(); URL.revokeObjectURL(a.href);
  };
  return (
    <div className="space-y-2" data-testid="segment-leden">
      <div className="flex items-center gap-2">
        <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Zoek op naam, klantcode, plaats, kenmerk…" className="h-8 text-sm" data-testid="segment-leden-zoek" />
        <span className="text-xs text-muted-foreground whitespace-nowrap tabular-nums" data-testid="segment-leden-aantal">{rijen.length} van {organisaties.length}</span>
        <Button variant="outline" size="sm" className="h-8" onClick={download} disabled={!rijen.length} data-testid="segment-leden-csv"><Download className="h-3.5 w-3.5 mr-1" />CSV</Button>
      </div>
      <div className="overflow-auto rounded-lg border border-border" style={{ maxHeight: maxHoogte }}>
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-card border-b border-border text-muted-foreground">
            <tr className="text-left [&>th]:px-2 [&>th]:py-1.5 [&>th]:font-medium">
              <th>Organisatie</th><th>Plaats</th><th>Status</th><th>Webshop</th><th className="text-right">Omzet 12m</th><th>Laatste order</th><th>Contact</th><th>Kenmerken</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rijen.map(o => (
              <tr key={o.id} className="hover:bg-muted/30 [&>td]:px-2 [&>td]:py-1.5 align-top" data-testid={`segment-lid-${o.id}`}>
                <td className="min-w-[180px]"><Link to={`/organisaties/${o.id}`} className="font-medium hover:underline">{o.naam}</Link>{o.klantcode && <div className="font-mono text-[10px] text-muted-foreground">{o.klantcode}</div>}</td>
                <td className="whitespace-nowrap">{o.plaats || "—"}{o.land ? ` · ${o.land}` : ""}</td>
                <td>{o.status || "—"}</td>
                <td>{o.webshop_status || "—"}</td>
                <td className="text-right tabular-nums whitespace-nowrap">{fmtEUR(o.omzet_12m || 0)}</td>
                <td className="whitespace-nowrap">{o.laatste_order_datum ? fmtDate(o.laatste_order_datum) : <span className="text-muted-foreground">geen order</span>}</td>
                <td className="whitespace-nowrap">
                  {o.heeft_email ? <span className="text-muted-foreground">{o.email}</span> : <Badge variant="destructive" className="h-4 text-[10px]">geen e-mail</Badge>}
                  {!o.heeft_telefoon && <Badge variant="outline" className="h-4 text-[10px] ml-1">geen tel.</Badge>}
                  {o.in_actieve_campagne && <Badge variant="secondary" className="h-4 text-[10px] ml-1">in campagne</Badge>}
                </td>
                <td className="max-w-[240px]"><div className="flex flex-wrap gap-0.5">{(o.kenmerken || []).slice(0, 6).map((k, i) => <Badge key={i} variant="outline" className="h-4 text-[10px] font-normal max-w-[150px] truncate inline-block leading-3" title={k}>{k}</Badge>)}{(o.kenmerken || []).length > 6 && <span className="text-[10px] text-muted-foreground">+{o.kenmerken.length - 6}</span>}</div></td>
              </tr>
            ))}
            {rijen.length === 0 && <tr><td colSpan={8} className="px-2 py-6 text-center text-muted-foreground">Geen organisaties gevonden.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function SegmentLedenDialog({ segment, onClose }) {
  const [orgs, setOrgs] = useState(null);
  useEffect(() => { if (segment) { setOrgs(null); api.get(`/segmenten/${segment.id}/organisaties`).then(r => setOrgs(r.data)).catch(() => setOrgs([])); } }, [segment]);
  return (
    <Dialog open={!!segment} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-5xl" data-testid="segment-leden-dialog">
        <DialogHeader><DialogTitle>{segment?.naam} <span className="text-muted-foreground font-normal text-sm">· {orgs ? orgs.length : segment?.aantal} organisaties</span></DialogTitle></DialogHeader>
        {orgs === null ? <div className="flex items-center gap-2 text-sm text-muted-foreground py-8 justify-center"><Loader2 className="h-4 w-4 animate-spin" />Lijst laden…</div>
          : <SegmentLedenLijst organisaties={orgs} naam={segment?.naam} />}
      </DialogContent>
    </Dialog>
  );
}
